import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

const VERSION_KEY = 'app_version';
/** Dernier rechargement forcé (horodatage) : évite une boucle si le rechargement ne suffit pas. */
const RELOAD_AT_KEY = 'app_version.reloadAt';
/** Délai minimal entre deux vérifications au retour au premier plan. */
const RECHECK_MIN_INTERVAL_MS = 60_000;

/**
 * Service qui vérifie la version de l'application au démarrage.
 * Récupère la version courante depuis la table AppSetting en base via l'API.
 * Si la version stockée en localStorage diffère de la version serveur,
 * seules les clés sensibles à un changement de schéma sont invalidées
 * (les préférences utilisateur, le thème, l'auth, etc. sont préservés).
 */
@Injectable({ providedIn: 'root' })
export class VersionService {

  private readonly apiUrl = `${environment.apiUrl}/api/app-settings`;

  /**
   * Whitelist explicite des clés à invalider lors d'un changement de version.
   * On préfère cette approche (whitelist) à un `localStorage.clear()` pour
   * ne pas perdre les préférences utilisateurs (thème, onglet actif, filtres,
   * conversations IA, etc.) à chaque déploiement.
   *
   * Ajouter ici toute clé dont la structure peut changer entre deux versions
   * (caches de payload API, états dénormalisés, etc.).
   */
  private readonly VERSION_SCOPED_KEYS: readonly string[] = [
    // Pour l'instant aucune clé n'est versionnée. Compléter au besoin lors
    // d'un changement breaking de schéma côté front.
  ];

  /** Version servie quand ce code a été chargé (null tant que l'API n'a pas répondu). */
  private bootVersion: string | null = null;
  private lastCheckAt = 0;
  /** Une version plus récente a été déployée depuis le chargement de la page. */
  updateAvailable = false;

  constructor(private http: HttpClient) {}

  /**
   * Revérifie la version serveur (retour de l'application au premier plan).
   * Sur iPhone, une page ouverte depuis l'écran d'accueil reste en mémoire des
   * jours sans être rechargée : elle continue d'exécuter l'ancienne version.
   */
  async recheck(): Promise<void> {
    if (this.updateAvailable) return;
    const now = Date.now();
    if (now - this.lastCheckAt < RECHECK_MIN_INTERVAL_MS) return;
    this.lastCheckAt = now;
    try {
      const serverVersion = await firstValueFrom(
        this.http.get(`${this.apiUrl}/AppVersion`, { responseType: 'text' })
      );
      // API injoignable au démarrage : la première réponse sert de référence.
      if (!this.bootVersion) {
        this.bootVersion = serverVersion;
        return;
      }
      if (serverVersion && serverVersion !== this.bootVersion) {
        console.info(`[VersionService] Version ${serverVersion} déployée (page chargée en ${this.bootVersion}) : rechargement à la prochaine navigation.`);
        this.updateAvailable = true;
      }
    } catch {
      // Hors ligne : on réessaiera au prochain retour au premier plan.
    }
  }

  /**
   * Recharge complètement la page sur l'URL demandée (nouvelle version ou fichier
   * de l'ancienne version supprimé). Retourne false si un rechargement forcé a eu
   * lieu il y a moins d'une minute (pas de boucle).
   */
  reloadTo(url: string): boolean {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_AT_KEY) || 0);
      if (Date.now() - last < RECHECK_MIN_INTERVAL_MS) return false;
      sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
    } catch { /* stockage indisponible : on recharge quand même */ }
    window.location.assign(url);
    return true;
  }

  /**
   * Vérifie si la version a changé et invalide les clés versionnées si nécessaire.
   * À appeler au démarrage de l'application (APP_INITIALIZER).
   * Retourne une Promise pour bloquer le démarrage le temps de la vérification.
   */
  async checkVersion(): Promise<void> {
    try {
      const serverVersion = await firstValueFrom(
        this.http.get(`${this.apiUrl}/AppVersion`, { responseType: 'text' })
      );

      this.bootVersion = serverVersion;
      this.lastCheckAt = Date.now();
      const storedVersion = localStorage.getItem(VERSION_KEY);

      if (storedVersion !== serverVersion) {
        console.info(
          `[VersionService] Nouvelle version détectée (${storedVersion ?? 'aucune'} → ${serverVersion}). Invalidation ciblée du cache.`
        );
        this.invalidateVersionScopedKeys();
        localStorage.setItem(VERSION_KEY, serverVersion);
      }
    } catch (error) {
      // En cas d'erreur réseau (API indisponible), on ne bloque pas le démarrage
      console.warn('[VersionService] Impossible de vérifier la version depuis le serveur.', error);
    }
  }

  /**
   * Supprime uniquement les clés sensibles à un changement de version,
   * en préservant l'ensemble du reste du localStorage.
   */
  private invalidateVersionScopedKeys(): void {
    for (const key of this.VERSION_SCOPED_KEYS) {
      localStorage.removeItem(key);
    }
  }
}
